import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../../layout/Card.jsx';
import { Icon } from '../../layout/Icon.jsx';
import { Pill } from '../../layout/Pill.jsx';
import { useAuth } from '../../shared/useAuth.js';
import { toast } from '../../shared/useToast.js';
import { api, apiError } from '../../shared/api.js';
import { formatDateTime } from '../../shared/format.js';
import { useAllowedWarehouses } from '../../shared/warehouseAccess.js';
import { useSettings } from '../../shared/useSettings.js';
import { useWarehouses } from '../products/hooks/useCategories.js';

import { SupplierPicker } from './components/SupplierPicker.jsx';
import { WarehousePicker } from './components/WarehousePicker.jsx';
import { ItemsGrid, lineMath } from './components/ItemsGrid.jsx';
import { TotalsSidebar } from './components/TotalsSidebar.jsx';
import { ConfirmModal } from '../pricing/components/ConfirmModal.jsx';
import {
  usePurchase,
  useCreatePurchase,
  useUpdatePurchase,
  useCreateAndPostPurchase,
  usePostPurchase,
  useCancelPurchase,
  invoiceIdOf
} from './hooks/usePurchases.js';

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function StatusPill({ status }) {
  if (status === 'posted') return <Pill variant="success">مرحّلة</Pill>;
  if (status === 'cancelled') return <Pill variant="danger">ملغاة</Pill>;
  return <Pill variant="warning">مسودّة</Pill>;
}

export function PurchaseInvoiceForm() {
  const { id: rawId } = useParams();
  // معرّف نصّي فاسد ('undefined') يعني تنقّلاً خاطئاً: نتعامل معه كفاتورة جديدة
  // بدل طلب فاتورة غير موجودة من الخادم.
  const id = rawId && rawId !== 'undefined' && rawId !== 'null' ? rawId : undefined;
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const hasPermission = useAuth((s) => s.hasPermission);
  const canCreate = hasPermission('inventory.purchase.create');
  const canPost = hasPermission('inventory.purchase.post');

  const { data: rawWarehouses = [] } = useWarehouses();
  const warehouses = useAllowedWarehouses(rawWarehouses);
  const { data: existing, isLoading } = usePurchase(id);
  // سياسة كلفة المنتج من إعدادات النظام (الافتراضي: الكلفة الأخيرة المدخلة).
  const { data: settings } = useSettings();
  const costPolicy = settings?.productCostPolicy === 'weighted' ? 'weighted' : 'last';

  const createMut = useCreatePurchase();
  const updateMut = useUpdatePurchase();
  const createAndPostMut = useCreateAndPostPurchase();
  const postMut = usePostPurchase();
  const cancelMut = useCancelPurchase();

  const [date, setDate] = useState(todayISO());
  const [receiveDate, setReceiveDate] = useState(todayISO());
  // تاريخ استحقاق سداد المورد — يبني عليه تقرير أعمار الديون.
  const [dueDate, setDueDate] = useState('');
  const [supplierId, setSupplierId] = useState('');
  const [supplierRef, setSupplierRef] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [paid, setPaid] = useState(0);
  // هل ثبّت المستخدم مبلغاً بيده؟ وقتها لا نعيد حسابه تلقائياً.
  const [paidTouched, setPaidTouched] = useState(false);
  const [cashboxId, setCashboxId] = useState('');
  // سياسة تحديث أسعار المنتج عند الشراء — اختيار صريح لا تحديث صامت.
  const [updateCost, setUpdateCost] = useState(false);
  const [updateSalePrice, setUpdateSalePrice] = useState(false);

  // قاصات الدفع — النقد الخارج للمورد يجب أن ينقص من قاصة محددة.
  const cashboxQuery = useQuery({
    queryKey: ['cashboxes', 'purchases'],
    queryFn: async () => {
      const { data } = await api.get('/cashbox');
      return data?.cashboxes || data?.data || data || [];
    },
    staleTime: 30_000
  });
  const cashboxes = useMemo(
    () => (Array.isArray(cashboxQuery.data) ? cashboxQuery.data : []).filter((cb) => cb.isActive !== false),
    [cashboxQuery.data]
  );
  const [errors, setErrors] = useState({});
  const [confirmCancel, setConfirmCancel] = useState(false);

  const status = existing?.status || 'draft';
  const readOnly = isEdit && (status !== 'draft' || !canCreate);

  // الإجمالي يُحسب من أسعار الفاتورة المكتوبة (لا من أسعار المنتج).
  const total = useMemo(() => {
    // نفس ترتيب الخادم: قبل الخصم ← ناقص خصومات المنتجات ← ناقص خصم الفاتورة.
    const lines = items.map((it) => lineMath(it));
    const subtotal = lines.reduce((s, l) => s + l.grossTotal, 0);
    const itemsDiscount = lines.reduce((s, l) => s + l.discountValue, 0);
    return Math.max(0, subtotal - itemsDiscount - (Number(discount) || 0));
  }, [items, discount]);

  useEffect(() => {
    // Default warehouse to first available
    if (!warehouseId && warehouses.length) {
      setWarehouseId(warehouses[0]._id || warehouses[0].id);
    }
  }, [warehouses, warehouseId]);

  // Pre-fill product from query string (?productId=...) — مرة واحدة فقط،
  // حتى لا تُمحى أسعار كتبها المستخدم لو أُعيد تنفيذ التأثير.
  const prefilledRef = useRef(false);
  useEffect(() => {
    const productId = params.get('productId');
    if (!productId || isEdit || prefilledRef.current) return;
    prefilledRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/catalog/products/${productId}`);
        if (cancelled || !data?._id) return;
        setWarehouseId(data.warehouseId?._id || data.warehouseId);
        setItems([{
          _key: Date.now(),
          productId: data._id,
          name: data.name,
          barcode: data.barcode || '',
          qty: 1,
          cost: data.costPrice || 0,
          salePrice: data.salePrice || 0,
          discount: 0,
          discountType: 'amount'
        }]);
      } catch (err) {
        toast.error(apiError(err));
      }
    })();
    return () => { cancelled = true; };
  }, [params, isEdit]);

  // Hydrate from existing invoice — مرة واحدة لكل فاتورة: أي إعادة جلب
  // (بعد نجاح/فشل عملية) كانت تكتب فوق ما يكتبه المستخدم الآن.
  const hydratedRef = useRef(null);
  useEffect(() => {
    if (!existing || !isEdit) return;
    const key = String(existing._id || existing.id || id);
    if (hydratedRef.current === key) return;
    hydratedRef.current = key;
    setDate(String(existing.date || '').slice(0, 10) || todayISO());
    setReceiveDate(existing.receiveDate ? String(existing.receiveDate).slice(0, 10) : '');
    setDueDate(existing.dueDate ? String(existing.dueDate).slice(0, 10) : '');
    setSupplierId(existing.supplierId?._id || existing.supplierId || '');
    setSupplierRef(existing.supplierRef || '');
    setWarehouseId(existing.warehouseId?._id || existing.warehouseId || '');
    setNotes(existing.notes || '');
    setItems((existing.items || []).map((it, i) => ({
      _key: i,
      productId: it.productId,
      name: it.productName || it.name,
      barcode: it.barcode || '',
      qty: it.qty,
      cost: it.cost,
      salePrice: it.salePrice,
      discount: it.discount || 0,
      discountType: it.discountType === 'percent' ? 'percent' : 'amount'
    })));
    setDiscount(existing.discount || 0);
    setPaymentMethod(existing.paymentMethod || 'cash');
    setPaid(existing.paid || 0);
    setPaidTouched(existing.paymentMethod === 'partial');
    setCashboxId(existing.cashboxId ? String(existing.cashboxId) : '');
  }, [existing, isEdit, id]);

  // المبلغ المدفوع يتبع الإجمالي تلقائياً حين تكون الطريقة «نقدي» (الدفع نقداً
  // = تسديد كامل المبلغ)، ويبقى صفراً في «آجل». يتوقف التتبّع إن ثبّت
  // المستخدم مبلغاً بنفسه.
  useEffect(() => {
    if (readOnly) return;
    if (paymentMethod === 'credit') {
      if (paid !== 0) setPaid(0);
      return;
    }
    if (paymentMethod === 'cash' && !paidTouched && paid !== total) setPaid(total);
  }, [paymentMethod, total, paidTouched, paid, readOnly]);

  function handlePaymentMethodChange(method) {
    setPaymentMethod(method);
    // تغيير الطريقة طلب صريح لإعادة الحساب.
    setPaidTouched(false);
    if (method === 'credit') setPaid(0);
    else if (method === 'cash') setPaid(total);
  }

  function handlePaidChange(value) {
    setPaidTouched(true);
    setPaid(Math.max(0, Math.round(Number(value) || 0)));
  }

  function buildBody() {
    return {
      supplierId,
      warehouseId,
      date,
      receiveDate: receiveDate || null,
      dueDate: dueDate || null,
      supplierRef: supplierRef || '',
      notes: notes || '',
      items: items
        .filter((it) => it.productId)
        .map((it) => ({
          productId: it.productId,
          qty: Math.max(1, Math.round(Number(it.qty) || 0)),
          // سعر الفاتورة كما كتبه المستخدم — لا يُستبدل بسعر المنتج.
          cost: Math.max(0, Math.round(Number(it.cost) || 0)),
          salePrice: Math.max(0, Math.round(Number(it.salePrice) || 0)),
          // خصم المنتج مستقل لكل صف، ونوعه يُرسَل معه.
          discount: Math.max(0, Number(it.discount) || 0),
          discountType: it.discountType === 'percent' ? 'percent' : 'amount'
        })),
      discount: Math.max(0, Math.round(Number(discount) || 0)),
      paymentMethod,
      paid: paymentMethod === 'credit' ? 0 : Math.max(0, Math.round(Number(paid) || 0)),
      // القاصة تُرسَل فقط عند وجود نقد خارج، وإلا null
      cashboxId: (paymentMethod === 'cash' || paymentMethod === 'partial') ? (cashboxId || null) : null
    };
  }

  function validate() {
    const e = {};
    if (!supplierId) e.supplierId = 'المورد مطلوب';
    if (!warehouseId) e.warehouseId = 'المخزن مطلوب';
    if (!items.length || !items.some((it) => it.productId)) e.items = 'يجب إضافة صنف واحد على الأقل';
    if ((paymentMethod === 'cash' || paymentMethod === 'partial') && !cashboxId) {
      e.cashboxId = 'اختر القاصة التي يُدفع منها المبلغ';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  /** يفرغ النموذج لفاتورة جديدة (بعد حفظ وترحيل ناجح). */
  function resetForm() {
    setDate(todayISO());
    setReceiveDate(todayISO());
    setDueDate('');
    setSupplierId('');
    setSupplierRef('');
    setNotes('');
    setItems([]);
    setDiscount(0);
    setPaymentMethod('cash');
    setPaid(0);
    setPaidTouched(false);
    setUpdateCost(false);
    setUpdateSalePrice(false);
    setErrors({});
  }

  // حارس إرسال واحد لكل الأزرار: نقرة مزدوجة (أو Enter متكرر) لا تُرسل الطلب
  // مرتين حتى في اللحظة التي لم تُعطّل فيها React الأزرار بعد — فلا فاتورة
  // مكرّرة ولا ترحيل مزدوج.
  const submittingRef = useRef(false);

  async function saveDraft() {
    if (submittingRef.current) return null;
    if (!validate()) return null;
    const body = buildBody();
    submittingRef.current = true;
    try {
      if (isEdit) {
        const res = await updateMut.mutateAsync({ id, body });
        // نافذة التعديل تُغلق بعد نجاح الحفظ: بقاؤها مفتوحة كان يسمح بحفظ
        // ثانٍ بلا داعٍ. القائمة تُحدَّث من invalidate داخل الـ hook فتظهر
        // التعديلات فوراً.
        navigate('/purchase', { replace: true });
        return res;
      }
      const res = await createMut.mutateAsync(body);
      const newId = invoiceIdOf(res);
      // بلا معرّف لا نتنقّل: التنقّل إلى /purchase/undefined كان يفتح نموذجاً
      // ميتاً ويطلب فاتورة غير موجودة من الخادم.
      if (newId) navigate(`/purchase/${newId}`, { replace: true });
      return res;
    } catch (_) {
      // الخطأ ظهر كتنبيه؛ النموذج يبقى مفتوحاً وبياناته كما هي.
      return null;
    } finally {
      submittingRef.current = false;
    }
  }

  async function saveAndPost() {
    if (submittingRef.current) return;
    if (!validate()) return;
    if (!canPost) {
      toast.warning('لا تملك صلاحية الترحيل');
      return;
    }
    submittingRef.current = true;
    try {
      if (isEdit) {
        await updateMut.mutateAsync({ id, body: buildBody() });
        await postMut.mutateAsync({ id, body: { updateCost, updateSalePrice } });
      } else {
        // حفظ + ترحيل في نداء واحد: لا تبقى مسوّدة تنتظر موافقة لاحقة.
        await createAndPostMut.mutateAsync({ ...buildBody(), updateCost, updateSalePrice });
      }
    } catch (_) {
      // الخطأ ظهر كتنبيه؛ تبقى بيانات المستخدم كما هي ولا يُفتح نموذج جديد.
      return;
    } finally {
      submittingRef.current = false;
    }
    // بعد ترحيل ناجح لفاتورة قائمة: نخرج من المحرّر إلى القائمة المحدَّثة.
    if (isEdit) navigate('/purchase', { replace: true });
    else resetForm();
  }

  async function handleCancelPosted() {
    if (!id) return;
    await cancelMut.mutateAsync(id, {
      onSuccess: () => setConfirmCancel(false)
    });
  }

  if (isEdit && isLoading) {
    return <Card><div className="text-ink-3 text-center py-8">جاري التحميل...</div></Card>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-extrabold tracking-tight m-0 flex items-center gap-2">
            {isEdit ? 'تعديل فاتورة شراء' : 'فاتورة شراء جديدة'}
            {existing?.number ? (
              <span className="text-ink-3 font-mono text-[14px]">{existing.number}</span>
            ) : null}
            {existing?.status ? <StatusPill status={existing.status} /> : null}
          </h2>
          <div className="text-ink-3 text-xs mt-0.5">
            المخزون › المشتريات › {isEdit ? (existing?.number || 'تعديل') : 'جديد'}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="btn" onClick={() => navigate('/purchase')}>
            <Icon name="chevronR" size={14} /> رجوع للقائمة
          </button>
          {isEdit && status === 'posted' && canPost ? (
            <button type="button" className="btn btn-danger" onClick={() => setConfirmCancel(true)}>
              <Icon name="x" size={14} /> إلغاء فاتورة
            </button>
          ) : null}
        </div>
      </div>

      <div className="grid gap-3" style={{ gridTemplateColumns: 'minmax(0,1fr) 360px' }}>
        <div className="space-y-3 min-w-0">
          <Card header="معلومات الفاتورة">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-ink-2 mb-1">رقم الفاتورة</label>
                <input
                  className="input"
                  dir="ltr"
                  style={{ background: '#FAF6EE', fontFamily: 'monospace', textAlign: 'right' }}
                  value={existing?.number || 'تلقائي'}
                  readOnly
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-2 mb-1">التاريخ</label>
                <input
                  className="input"
                  type="date"
                  value={date}
                  readOnly={readOnly}
                  onChange={(e) => setDate(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-2 mb-1">تاريخ الاستلام</label>
                <input
                  className="input"
                  type="date"
                  value={receiveDate || ''}
                  readOnly={readOnly}
                  onChange={(e) => setReceiveDate(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-2 mb-1">تاريخ استحقاق السداد</label>
                <input
                  className="input"
                  type="date"
                  value={dueDate || ''}
                  readOnly={readOnly}
                  onChange={(e) => setDueDate(e.target.value)}
                />
                <div className="text-[10.5px] text-ink-3 mt-0.5">اختياري — يُبنى عليه تقرير أعمار ديون الموردين.</div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-2 mb-1">المورد *</label>
                <SupplierPicker
                  value={supplierId}
                  onChange={readOnly ? () => {} : setSupplierId}
                  error={errors.supplierId}
                  allowCreate={!readOnly}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-ink-2 mb-1">رقم وصل المورد</label>
                <input
                  className="input"
                  placeholder="اختياري"
                  value={supplierRef}
                  readOnly={readOnly}
                  onChange={(e) => setSupplierRef(e.target.value)}
                />
              </div>
              <div className="col-span-3">
                <label className="block text-xs font-semibold text-ink-2 mb-1">المخزن *</label>
                <WarehousePicker
                  value={warehouseId}
                  onChange={readOnly ? () => {} : setWarehouseId}
                  warehouses={warehouses}
                  error={errors.warehouseId}
                />
              </div>
            </div>
          </Card>

          <ItemsGrid items={items} onChange={setItems} warehouseId={warehouseId} readOnly={readOnly} />
          {errors.items ? (
            <div className="text-red text-[12px] font-semibold">{errors.items}</div>
          ) : null}

          <Card header="ملاحظات">
            <textarea
              className="input"
              rows={2}
              value={notes}
              readOnly={readOnly}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="أي تفاصيل عن الفاتورة..."
            />
          </Card>

          {existing?.postedAt ? (
            <Card>
              <div className="text-[12.5px] text-ink-2">
                تم الترحيل: <b>{formatDateTime(existing.postedAt)}</b>
                {existing.postedById?.fullName ? <> بواسطة <b>{existing.postedById.fullName}</b></> : null}
              </div>
            </Card>
          ) : null}
        </div>

        <TotalsSidebar
          items={items}
          discount={discount}
          onDiscountChange={setDiscount}
          paymentMethod={paymentMethod}
          onPaymentMethodChange={handlePaymentMethodChange}
          paid={paid}
          onPaidChange={handlePaidChange}
          cashboxId={cashboxId}
          onCashboxChange={setCashboxId}
          cashboxes={cashboxes}
          cashboxesLoading={cashboxQuery.isLoading}
          updateCost={updateCost}
          onUpdateCostChange={setUpdateCost}
          updateSalePrice={updateSalePrice}
          onUpdateSalePriceChange={setUpdateSalePrice}
          costPolicy={costPolicy}
          readOnly={readOnly}
          isEdit={isEdit}
          saving={createMut.isPending || updateMut.isPending}
          posting={postMut.isPending || createAndPostMut.isPending}
          onSaveDraft={saveDraft}
          onSaveAndPost={saveAndPost}
          onCancel={() => navigate('/purchase')}
        />
      </div>

      <ConfirmModal
        open={confirmCancel}
        title="إلغاء فاتورة مرحّلة"
        message={`سيتم إنشاء حركة مرتجعة لجميع الأصناف وعكس رصيد المورد. هل أنت متأكد؟`}
        confirmLabel="إلغاء الفاتورة"
        variant="danger"
        loading={cancelMut.isPending}
        onClose={() => setConfirmCancel(false)}
        onConfirm={handleCancelPosted}
      />
    </div>
  );
}
